import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { QuoteDetailsForm } from "@/components/QuoteDetailsForm";
import { CoveragePicker } from "@/components/CoveragePicker";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { DropZone } from "@/components/DropZone";
import { computeFiscalCode } from "@/lib/fiscalCode";
import { validatePlate, validateItalianMobile, normalizeMobile } from "@/lib/api";
import { validateItalianVAT } from "@/lib/vatValidation";
import { Sparkles, CheckCircle2, ArrowRight, ArrowLeft, ShieldCheck, Loader2, Upload, AlertCircle, User, Phone, MapPin as MapPinIcon, FileText, Paperclip, Car } from "lucide-react";
import { LiveClock } from "@/components/LiveClock";
import { toast } from "sonner";
import PoweredByStrip from "@/components/PoweredByStrip";
import { usePolicyTypes } from "@/lib/usePolicyTypes";
import { DatePicker } from "@/components/DatePicker";

// Badge "↑ libretto": visibile per i campi auto-compilati dall'estrazione AI.
const FilledBadge = ({ active }) => {
  if (!active) return null;
  return (
    <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 uppercase tracking-wide align-middle" title="Compilato automaticamente dal libretto">
      ↑ libretto
    </span>
  );
};

// 🆕 Mapping key tecnica → etichetta italiana leggibile per il riepilogo di conferma
const DETAIL_LABEL_MAP = {
  vehicle_brand: "Marca veicolo",
  vehicle_model: "Modello",
  vehicle_version: "Versione",
  vehicle_plate: "Targa",
  vehicle_type: "Tipo veicolo",
  vehicle_fuel: "Alimentazione",
  vehicle_additional_fuel: "Alimentazione aggiuntiva",
  vehicle_displacement: "Cilindrata (cc)",
  vehicle_kw: "Potenza (KW)",
  vehicle_hp: "Potenza (CV)",
  vehicle_seats: "Posti a sedere",
  vehicle_weight: "Massa (kg)",
  vehicle_registration_date: "Data immatricolazione",
  vehicle_purchase_date: "Data acquisto",
  vehicle_value: "Valore veicolo (€)",
  vehicle_chassis: "Telaio",
  vehicle_use: "Uso veicolo",
  annual_km: "Km annui",
  use: "Uso del veicolo",
  parking: "Custodia",
  anti_theft: "Antifurto",
  birth_date: "Data nascita conducente",
  youngest_driver_age: "Età conducente più giovane",
  license_first_issue: "Data primo rilascio patente",
  driving_type: "Tipo di guida",
  education: "Titolo di studio",
  profession: "Professione conducente",
  merit_class: "Classe di merito attuale",
  years_insured: "Anni assicurato",
  claims_last_5y: "Sinistri ultimi 5 anni",
  current_company: "Compagnia attuale",
  current_policy_expiry: "Scadenza polizza attuale",
  insurance_status: "Situazione assicurativa",
  bersani_applicable: "Legge Bersani / RC Familiare",
  bersani_type: "Tipo beneficio Bersani",
  bersani_relative_kinship: "Grado di parentela",
  bersani_relative_class: "Classe di merito ereditata",
  bersani_relative_plate: "Targa veicolo familiare",
  bersani_relative_company: "Compagnia del familiare",
  bersani_relative_fiscal_code: "Codice Fiscale del familiare",
  other_quotes_done: "Ha già richiesto altri preventivi",
  coverage_notes: "Note copertura",
};

// 🆕 Mapping valore tecnico → label italiana (per codici di lookup lists)
const DETAIL_VALUE_MAP = {
  // Situazione assicurativa
  already_insured: "Veicolo già assicurato",
  new_vehicle: "Nuovo veicolo da assicurare",
  // Km annui
  "0_5000": "Fino a 5.000 km",
  "5000_10000": "5.000 – 10.000 km",
  "10000_15000": "10.000 – 15.000 km",
  "15000_20000": "15.000 – 20.000 km",
  "20000_30000": "20.000 – 30.000 km",
  over_30000: "Oltre 30.000 km",
  // Antifurto
  nessuno: "Nessuno",
  immobilizer: "Immobilizer di serie",
  allarme: "Allarme acustico",
  satellitare: "Satellitare (GPS)",
  meccanico: "Antifurto meccanico",
  misto: "Misto (elettronico + meccanico)",
  // Uso
  privato: "Privato",
  professionale: "Professionale",
  autonoleggio: "Autonoleggio",
  // Custodia
  box: "Box / Garage",
  posto_auto_coperto: "Posto auto coperto",
  strada: "Strada / All'aperto",
  cortile: "Cortile privato",
  // Tipo guida
  esclusiva: "Esclusiva",
  esperta: "Esperta",
  libera: "Libera",
  // Bersani
  bersani: "Bersani (nuova immatricolazione o cambio veicolo)",
  rc_familiare: "RC Familiare (veicolo già assicurato)",
  // Yes/No booleans → in italiano
  true: "Sì",
  false: "No",
};

// 🆕 Formatta un valore per il riepilogo: mappa codice→label, boolean→Sì/No, date ISO→DD/MM/YYYY
const formatDetailValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (Array.isArray(v)) return v.map((x) => DETAIL_VALUE_MAP[x] || x).join(", ");
  const s = String(v);
  // ISO date YYYY-MM-DD → DD/MM/YYYY
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return DETAIL_VALUE_MAP[s] || s;
};

// 🆕 Umanizza chiave sconosciuta (snake_case → Title Case IT)
const humanizeKey = (k) => {
  if (DETAIL_LABEL_MAP[k]) return DETAIL_LABEL_MAP[k];
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// 🆕 Raggruppa i dettagli per macro-sezione visiva del riepilogo
const groupDetails = (details) => {
  const groups = {
    vehicle: { title: "🚗 Veicolo", color: "sky", keys: ["vehicle_brand", "vehicle_model", "vehicle_version", "vehicle_plate", "vehicle_type", "vehicle_fuel", "vehicle_additional_fuel", "vehicle_displacement", "vehicle_kw", "vehicle_hp", "vehicle_seats", "vehicle_registration_date", "vehicle_purchase_date", "vehicle_value", "vehicle_chassis", "vehicle_use", "annual_km", "use", "parking", "anti_theft"], items: [] },
    driver: { title: "👤 Conducente", color: "amber", keys: ["birth_date", "youngest_driver_age", "license_first_issue", "driving_type", "education", "profession"], items: [] },
    history: { title: "📊 Storico assicurativo", color: "teal", keys: ["insurance_status", "merit_class", "years_insured", "claims_last_5y", "current_company", "current_policy_expiry"], items: [] },
    bersani: { title: "⚖️ Legge Bersani / RC Familiare", color: "blue", keys: ["bersani_applicable", "bersani_type", "bersani_relative_kinship", "bersani_relative_class", "bersani_relative_plate", "bersani_relative_fiscal_code", "bersani_relative_company"], items: [] },
    other: { title: "📋 Altre informazioni", color: "slate", keys: [], items: [] },
  };
  const known = new Set(Object.values(groups).flatMap((g) => g.keys));
  Object.entries(details || {}).forEach(([k, v]) => {
    if (k.startsWith("_") || k === "selected_coverages") return;
    if (v === null || v === undefined || v === "") return;
    const target = Object.values(groups).find((g) => g.keys.includes(k)) || groups.other;
    target.items.push([k, v]);
  });
  void known;
  return Object.values(groups).filter((g) => g.items.length > 0);
};

export default function PublicQuoteRequest() {
  const { agencySlug: agencySlugFromUrl } = useParams();
  // Se lo slug manca dall'URL (es. /richiedi-preventivo senza suffisso), lo risolviamo dal backend
  const [resolvedSlug, setResolvedSlug] = useState(null);
  const agencySlug = agencySlugFromUrl || resolvedSlug;

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  // Risolve lo slug di default una sola volta se non è nell'URL
  useEffect(() => {
    if (agencySlugFromUrl) return; // già presente
    if (resolvedSlug) return; // già risolto
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/public/default-agency-slug`);
        if (!cancelled && data?.slug) setResolvedSlug(data.slug);
      } catch { /* nessuna agenzia configurata */ }
    })();
    return () => { cancelled = true; };
  }, [agencySlugFromUrl, apiBase, resolvedSlug]);

  const [agencyPage, setAgencyPage] = useState(null);
  // 🆕 Feb 2026 — Fallback logo: se l'immagine fallisce a caricarsi (URL rotto, storage privato,
  // dominio non raggiungibile) mostriamo l'icona placeholder invece della broken-image del browser.
  const [logoBroken, setLogoBroken] = useState(false);
  // Risolve l'URL del logo: se path relativo (/api/...) prefissa REACT_APP_BACKEND_URL,
  // se http(s) usa così, altrimenti (path storage interno legacy) considera come rotto.
  const resolveLogoSrc = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${s}`;
    return "";
  };
  const { options: TYPES_ALL, findLabel: typeLabel } = usePolicyTypes(true);
  const TYPES = (() => {
    const allowed = agencyPage?.enabled_quote_types;
    if (!allowed || allowed.length === 0) return TYPES_ALL; // tutte abilitate
    return TYPES_ALL.filter((t) => allowed.includes((t.v || t.value || "").toLowerCase()));
  })();
  const [step, setStep] = useState(1);
  const [client, setClient] = useState({
    client_kind: "person",  // 🆕 "person" o "company"
    business_name: "", vat_number: "", pec: "", referente_name: "", referente_role: "",
    name: "", surname: "", email: "", phone: "", mobile: "", fiscal_code: "",
    address: "", house_number: "", city: "", province: "", cap: "", birth_date: "", gender: "M",
    birth_place: "", belfiore_code: "",
    marital_status: "",
    profession: "",
  });
  const [quote, setQuote] = useState({ type: "auto", coverage_request: "", details: {} });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  // Estrazione AI libretto (drag&drop)
  const [extracting, setExtracting] = useState(false);
  const [extractedSummary, setExtractedSummary] = useState(null); // { vehicleCount, ownerCount, extras: [{key,value}] }
  const [extractionError, setExtractionError] = useState(null);
  // Set dei nomi campi cliente compilati automaticamente dal libretto.
  // Quando l'utente modifica manualmente un campo, lo rimuoviamo dal set.
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  // Allegati pubblici (PDF/immagini): vengono caricati on-the-fly e collegati al preventivo al submit
  const [attachments, setAttachments] = useState([]);  // [{id, filename, size, content_type}]
  const [uploadingAtt, setUploadingAtt] = useState(false);
  // 🆕 Feb 2026 — Riferimenti agli allegati "Libretto Bersani" e "Libretto RC Familiare"
  //    per poter mostrare la preview e permettere la rimozione mirata.
  const [bersaniLibrettoId, setBersaniLibrettoId] = useState(null);
  const [rcFamLibrettoId, setRcFamLibrettoId] = useState(null);
  const [requiredDocs, setRequiredDocs] = useState([]);
  // Riepilogo di conferma prima di inviare
  const [reviewOpen, setReviewOpen] = useState(false);
  // Consenso privacy obbligatorio per inviare la richiesta
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  // Consenso marketing opzionale (GDPR — doppio consenso)
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  // CAP reverse lookup: dato un CAP digitato, suggerisce città+provincia
  const [capSuggestions, setCapSuggestions] = useState([]);
  const [capLookupLoading, setCapLookupLoading] = useState(false);
  // 🆕 Gradi di parentela dell'agenzia (per Bersani / RC Familiare)
  const [kinshipDegrees, setKinshipDegrees] = useState([]);
  // 🆕 Feb 2026 — Liste professione (lookup pubblica per l'agenzia)
  const [professionOptions, setProfessionOptions] = useState([]);
  // 🆕 Feb 2026 — Nuove lookup pubbliche per il form richiesta preventivo
  const [annualMileageOptions, setAnnualMileageOptions] = useState([]);
  const [antitheftOptions, setAntitheftOptions] = useState([]);
  const [educationOptions, setEducationOptions] = useState([]);
  const [currentCompanyOptions, setCurrentCompanyOptions] = useState([]);
  const [insuranceStatusOptions, setInsuranceStatusOptions] = useState([]);
  // 🆕 Feb 2026 — Attestato di rischio altro veicolo (dinamico, controllato dai flag della voce `insurance_status`)
  const [bersaniSourceOptions, setBersaniSourceOptions] = useState([]);

  // 🆕 Carica i gradi di parentela configurati dall'agenzia
  useEffect(() => {
    if (!agencySlug) { setKinshipDegrees([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/public/kinship-degrees/${agencySlug}`);
        if (!cancelled) setKinshipDegrees(data || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [agencySlug, apiBase]);

  // 🆕 Feb 2026 — Carica la lookup pubblica "profession" per l'agenzia
  // Cache-busting con timestamp per evitare che il browser/CDN serva liste vecchie
  const [professionLoading, setProfessionLoading] = useState(false);
  useEffect(() => {
    if (!agencySlug) { setProfessionOptions([]); return; }
    let cancelled = false;
    setProfessionLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/public/lookup-lists/${agencySlug}/profession`, { params: { _t: Date.now() } });
        if (!cancelled) setProfessionOptions(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setProfessionOptions([]); }
      finally { if (!cancelled) setProfessionLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [agencySlug, apiBase]);

  // 🆕 Carica tutte le altre lookup lists pubbliche (Km annui, Antifurto, Titolo studio, Compagnia, Situazione assicurativa)
  useEffect(() => {
    if (!agencySlug) return;
    let cancelled = false;
    const loadList = async (key, setter) => {
      try {
        const { data } = await axios.get(`${apiBase}/public/lookup-lists/${agencySlug}/${key}`, { params: { _t: Date.now() } });
        if (!cancelled) setter(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setter([]); }
    };
    loadList("annual_mileage", setAnnualMileageOptions);
    loadList("antitheft", setAntitheftOptions);
    loadList("education", setEducationOptions);
    loadList("current_company", setCurrentCompanyOptions);
    loadList("insurance_status", setInsuranceStatusOptions);
    loadList("bersani_source", setBersaniSourceOptions);
    return () => { cancelled = true; };
  }, [agencySlug, apiBase]);

  // Personalizzazione area pubblica per agenzia (titolo, colore, logo, etc.)
  useEffect(() => {
    if (!agencySlug) { setAgencyPage(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/public/agency-page/${agencySlug}`);
        if (!cancelled) setAgencyPage(data);
      } catch { setAgencyPage(null); }
    })();
    return () => { cancelled = true; };
  }, [agencySlug, apiBase]);

  // Carica i documenti richiesti in base al tipo polizza selezionato
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/public/required-documents`, { params: { type: quote.type } });
        if (!cancelled) setRequiredDocs(data || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [quote.type, apiBase]);

  // CAP reverse lookup: appena l'utente digita 5 cifre, cerca città+provincia
  useEffect(() => {
    const cap = (client.cap || "").trim();
    if (!/^\d{5}$/.test(cap)) {
      setCapSuggestions([]);
      return;
    }
    // Skip se già coerente con città attuale (evita loop)
    let cancelled = false;
    setCapLookupLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${apiBase}/public/cap-lookup`, { params: { cap } });
        if (cancelled) return;
        const results = Array.isArray(data) ? data : [];
        if (results.length === 1) {
          // Unico match: auto-fill se città/provincia attualmente vuote o diverse
          const r = results[0];
          if (!client.city || client.city.toLowerCase() !== (r.name || "").toLowerCase()) {
            updateClient("city", r.name);
          }
          if (!client.province || client.province !== r.province) {
            updateClient("province", r.province);
          }
          setCapSuggestions([]);
        } else if (results.length > 1) {
          setCapSuggestions(results);
        } else {
          setCapSuggestions([]);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setCapLookupLoading(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [client.cap, apiBase]);

  const uploadAttachment = async (file, description = "") => {
    if (!file) return null;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10 MB)");
      return null;
    }
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${apiBase}/public/upload-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // 🆕 Se descrizione fornita (es. da estrazione AI), la associo all'allegato
      const enriched = description ? { ...data, description } : data;
      setAttachments((prev) => [...prev, enriched]);
      toast.success(description ? `Allegato: ${description}` : `Allegato: ${data.filename}`);
      return enriched;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
      return null;
    } finally {
      setUploadingAtt(false);
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  // Mappa i campi conosciuti del libretto verso details (veicolo) e client (intestatario).
  const VEHICLE_KEYS = new Set([
    "vehicle_plate", "vehicle_brand", "vehicle_model", "vehicle_version",
    "vehicle_year", "vehicle_first_registration", "fuel", "displacement",
    "kw", "cv", "seats", "max_mass", "co2_emissions",
  ]);
  const OWNER_TO_CLIENT = {
    owner_name: "name",
    owner_surname: "surname",
    owner_fiscal_code: "fiscal_code",
    owner_birth_date: "birth_date",
    owner_birth_place: "birth_place",
    owner_address: "address",
    owner_city: "city",
    owner_province: "province",
    owner_cap: "cap",
    owner_email: "email",
    owner_phone: "mobile",
    owner_gender: "gender",
    owner_marital_status: "marital_status",
  };

  const extractFromLibretto = async (file) => {
    if (!file || extracting) return;
    setExtracting(true);
    setExtractionError(null);
    setExtractedSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${apiBase}/public/extract-libretto`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = (data && data.extracted) || {};
      const cleaned = Object.fromEntries(
        Object.entries(d).filter(([, v]) => v !== null && v !== undefined && v !== ""),
      );
      if (Object.keys(cleaned).length === 0) {
        setExtractionError("L'AI non è riuscita a leggere alcun dato dal libretto. Prova con una foto più nitida.");
        return;
      }
      // Suddividi i campi estratti
      const vehicleUpdates = {};
      const clientUpdates = {};
      const extras = [];
      let vehicleCount = 0;
      let ownerCount = 0;
      Object.entries(cleaned).forEach(([k, v]) => {
        if (VEHICLE_KEYS.has(k)) {
          vehicleUpdates[k] = v;
          vehicleCount += 1;
        } else if (OWNER_TO_CLIENT[k]) {
          clientUpdates[OWNER_TO_CLIENT[k]] = v;
          ownerCount += 1;
        } else if (k !== "previous_owners" && k !== "owner_house_number" && k !== "ownership_date") {
          extras.push({ key: k, value: typeof v === "object" ? JSON.stringify(v) : String(v) });
        }
        if (k === "owner_house_number" && v) {
          // Concatena al campo address se presente
          extras.push({ key: "house_number", value: String(v) });
        }
      });

      // Applica: nei campi cliente solo se vuoti (preserva input manuale precedente)
      const filledKeys = [];
      setClient((c) => {
        const next = { ...c };
        Object.entries(clientUpdates).forEach(([key, val]) => {
          if (!c[key] || String(c[key]).trim() === "") {
            next[key] = String(val);
            filledKeys.push(key);
          }
        });
        // Concatena civico all'indirizzo se presente in extras e address non lo include già
        const hn = extras.find((x) => x.key === "house_number");
        if (hn && next.address && !next.address.match(/\d/)) {
          next.address = `${next.address}, ${hn.value}`;
          if (!filledKeys.includes("address")) filledKeys.push("address");
        }
        return next;
      });
      setAutoFilledFields((prev) => {
        const merged = new Set(prev);
        filledKeys.forEach((k) => merged.add(k));
        return merged;
      });
      // Details: aggiorna sempre (estrazione veicolo è più affidabile dell'esistente)
      setQuote((q) => ({
        ...q,
        type: "auto",
        details: {
          ...(q.details || {}),
          ...vehicleUpdates,
          _libretto_extras: extras.length > 0 ? extras : undefined,
        },
      }));
      setExtractedSummary({ vehicleCount, ownerCount, extrasCount: extras.length });
      toast.success(`Estratti ${vehicleCount + ownerCount} campi dal libretto`);
      // 🆕 Allega anche il file estratto tra i documenti allegati, con descrizione "Libretto (estratto AI)"
      // Così l'utente lo vede sotto "Documenti allegati" e il documento richiesto viene automaticamente spuntato.
      try {
        await uploadAttachment(file, "Libretto veicolo (estratto AI)");
      } catch { /* silent — l'estrazione è comunque riuscita */ }
    } catch (e) {
      setExtractionError(e?.response?.data?.detail || "Estrazione fallita");
    } finally {
      setExtracting(false);
    }
  };

  const tryAutoFiscalCode = () => {
    const cf = computeFiscalCode({
      name: client.name,
      surname: client.surname,
      gender: client.gender,
      birth_date: client.birth_date,
      belfiore_code: client.belfiore_code,
    });
    if (cf) {
      setClient((c) => ({ ...c, fiscal_code: cf }));
      toast.success(`Codice fiscale calcolato: ${cf}`);
    } else {
      toast.error("Compila Nome, Cognome, Sesso, Data nascita e Codice catastale (es. H501)");
    }
  };

  // Aggiorna un campo cliente e rimuove l'evidenziazione "autocompilato" se l'utente modifica.
  const updateClient = (key, value) => {
    setClient((c) => ({ ...c, [key]: value }));
    setAutoFilledFields((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // Helper che restituisce un FilledBadge attivo se il campo è stato auto-compilato.
  // eslint-disable-next-line react/no-unstable-nested-components
  const Filled = (field) => <FilledBadge active={autoFilledFields.has(field)} />;

  // 🆕 Elenco errori campi mancanti/non validi (mostrato in banner in cima allo step)
  const [formErrors, setFormErrors] = useState([]);

  const validateStep1 = () => {
    const errors = [];
    // 🆕 Se azienda: obbligatori ragione sociale + P.IVA
    if (client.client_kind === "company") {
      if (!(client.business_name || "").trim()) errors.push("Ragione sociale obbligatoria");
      const vv = validateItalianVAT(client.vat_number || "");
      if (!vv.ok) errors.push(vv.message);
    }
    const labels = { name: "Nome referente", surname: "Cognome referente", mobile: "Cellulare", email: "Email", birth_date: "Data di nascita", gender: "Sesso", birth_place: "Luogo di nascita", fiscal_code: "Codice fiscale", marital_status: "Stato civile", profession: "Professione" };
    const baseFields = ["name", "surname", "mobile", "email"];
    // 🆕 Per persone fisiche: tutti i dati personali sono obbligatori (data nascita, sesso, luogo, CF, stato civile, professione)
    const personalFields = client.client_kind === "company" ? [] : ["birth_date", "gender", "birth_place", "fiscal_code", "marital_status", "profession"];
    for (const f of [...baseFields, ...personalFields]) {
      if (!(client[f] || "").toString().trim()) errors.push(`${labels[f]} obbligatorio`);
    }
    if (client.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) errors.push("Email non valida");
    if (client.mobile) {
      const mv = validateItalianMobile(client.mobile);
      if (!mv.ok) errors.push(`Cellulare: ${mv.message}`);
    }
    if (client.fiscal_code && client.fiscal_code.replace(/\s/g, "").length !== 16) errors.push("Codice fiscale: deve essere di 16 caratteri");
    if (client.cap && !/^\d{5}$/.test(client.cap.toString().trim())) errors.push("CAP: 5 cifre o lasciare vuoto");
    // 🆕 Se data di nascita compilata, il conducente deve avere almeno 18 anni
    if (client.birth_date && /^\d{4}-\d{2}-\d{2}$/.test(client.birth_date)) {
      const [y, m, d] = client.birth_date.split("-").map(Number);
      const bdDate = new Date(y, m - 1, d);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (bdDate > today) {
        errors.push("Data di nascita: non può essere nel futuro");
      } else {
        let age = today.getFullYear() - y;
        const md = (today.getMonth() + 1) - m;
        if (md < 0 || (md === 0 && today.getDate() < d)) age -= 1;
        if (age < 18) errors.push(`Data di nascita: il conducente deve avere almeno 18 anni (età rilevata: ${age})`);
      }
    }
    if (errors.length > 0) {
      setFormErrors(errors);
      toast.error(`Compila ${errors.length} ${errors.length === 1 ? "campo obbligatorio" : "campi obbligatori"}`);
      // Scroll to top per vedere il banner errori
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    setFormErrors([]);
    return true;
  };

  const openReview = () => {
    const errors = [];
    if (!quote.coverage_request.trim()) errors.push('Descrivi cosa vuoi assicurare (campo "Cosa vuoi assicurare?")');
    if (quote.type === "auto" || quote.type === "moto") {
      const plate = (quote.details?.vehicle_plate || "").trim();
      if (!plate) {
        errors.push("Targa del veicolo obbligatoria");
      } else {
        const pv = validatePlate(plate);
        if (!pv.ok) errors.push(`Targa non valida: ${pv.message}`);
      }
      if (!quote.details?.insurance_status) errors.push('Situazione assicurativa obbligatoria (Veicolo già assicurato / Nuovo veicolo da assicurare)');
      if (!quote.details?.vehicle_purchase_date) errors.push("Data di acquisto del veicolo obbligatoria");
    }
    if (quote.details?.other_quotes_done === true) {
      if (!quote.details.other_quotes_amount) errors.push("Importo del miglior preventivo già ricevuto");
      if (!quote.details.other_quotes_frazionamento) errors.push("Frazionamento del miglior preventivo");
    }
    if (errors.length > 0) {
      setFormErrors(errors);
      toast.error(`Compila ${errors.length} ${errors.length === 1 ? "campo obbligatorio" : "campi obbligatori"} prima di inviare`);
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setFormErrors([]);
    setReviewOpen(true);
  };

  const submit = async () => {
    if (!privacyAccepted) { toast.error("Devi accettare la Privacy Policy per inviare la richiesta"); return; }
    setReviewOpen(false);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${apiBase}/public/quote-request`, {
        agency_slug: agencySlug || undefined,
        client,
        quote,
        attachment_ids: attachments.map((a) => a.id),
        privacy_consent: {
          accepted: true,
          accepted_at: new Date().toISOString(),
          version: "v1",
        },
        marketing_consent: {
          accepted: marketingAccepted,
          accepted_at: marketingAccepted ? new Date().toISOString() : null,
          version: "v1",
        },
      });
      setDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-slate-200 max-w-lg w-full p-8 text-center shadow-sm">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">Richiesta inviata!</h2>
          <p className="text-slate-600 mb-6">
            Grazie {client.name}, abbiamo ricevuto la tua richiesta di preventivo. Un consulente ti contatterà al più presto via email o telefono.
          </p>
          <div className="bg-slate-50 rounded-md p-3 text-xs text-slate-500 mb-6">
            Riferimento: <span className="font-mono">{done.quote_id?.slice(0, 8)}</span>
          </div>
          <Button onClick={() => window.location.href = "/"} className="bg-[#0F172A] hover:bg-[#1E293B]">Torna alla home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50/40 to-emerald-50/30" data-testid="public-quote-page" style={agencyPage?.primary_color ? { ["--agency-color"]: agencyPage.primary_color } : {}}>
      <PoweredByStrip testId="pqr-powered-by" />
      <header className="bg-white/80 backdrop-blur border-b border-blue-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {(() => {
              const logoSrc = resolveLogoSrc(agencyPage?.logo_url);
              if (logoSrc && !logoBroken) {
                return (
                  <img
                    src={logoSrc}
                    alt={agencyPage?.agency_name || "Logo agenzia"}
                    className="w-10 h-10 rounded-lg object-cover shadow-sm bg-white"
                    onError={() => setLogoBroken(true)}
                    data-testid="pqr-agency-logo"
                  />
                );
              }
              return (
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shadow-sm" style={{ background: agencyPage?.primary_color || "linear-gradient(135deg,#2563EB,#4F46E5)" }} data-testid="pqr-agency-logo-fallback">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
              );
            })()}
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: agencyPage?.primary_color || "#2563EB" }}>
                {agencyPage?.agency_name || "Studio di Consulenza Amoroso"}
              </p>
              <h1 className="font-display text-lg font-semibold text-slate-900">
                {agencyPage?.title || "Richiedi un preventivo"}
              </h1>
            </div>
          </div>
          {/* 🆕 Nascosto su mobile per liberare spazio (visibile da tablet in su) */}
          <div className="hidden sm:inline-block bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs">
            <LiveClock inline />
          </div>
        </div>
        {agencyPage?.subtitle && (
          <div className="max-w-6xl mx-auto px-4 pb-3 text-sm text-slate-600">
            {agencyPage.subtitle}
          </div>
        )}
        {agencyPage?.intro && (
          <div className="max-w-6xl mx-auto px-4 pb-3 text-xs text-slate-500 italic border-t border-slate-100 pt-2">
            {agencyPage.intro}
          </div>
        )}
      </header>

      {/* Banner chiusure attive (clienti) */}
      {agencyPage?.active_closures?.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pt-4 space-y-2" data-testid="public-closure-banner">
          {agencyPage.active_closures.map((cl, i) => (
            <div key={i} className="rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-rose-50 p-3 flex items-start gap-3">
              <span className="text-2xl shrink-0">{cl.kind === "summer" ? "🌴" : cl.kind === "holiday" ? "🎄" : "🔒"}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">{cl.title}</p>
                {cl.message && <p className="text-xs text-amber-800/90 mt-0.5 leading-relaxed">{cl.message}</p>}
                {cl.hours && <p className="text-xs text-rose-800 mt-0.5">⏱ {cl.hours}</p>}
                <p className="text-[10px] text-amber-700/80 mt-1">
                  Dal {new Date(cl.start_at).toLocaleDateString("it-IT")} al {new Date(cl.end_at).toLocaleDateString("it-IT")}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tempo stimato + reassurance banner */}
        <div className="flex items-center justify-between gap-3 mb-4 bg-gradient-to-r from-emerald-50 via-emerald-50/60 to-transparent border border-emerald-200 rounded-lg px-3.5 py-2.5" data-testid="pub-time-estimate">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="text-base">⏱</span>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-emerald-900 truncate">
                Compilazione: ~30 secondi
              </div>
              <div className="text-[11px] text-emerald-700 truncate">
                Bastano 4 dati per iniziare. Ti ricontatteremo entro 24 ore.
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-emerald-700 flex-shrink-0">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="font-medium">100% sicuro · GDPR</span>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-5">
          <Step n={1} active={step === 1} done={step > 1} label="I tuoi dati" />
          <div className={`flex-1 h-0.5 mx-3 ${step > 1 ? "bg-blue-500" : "bg-slate-200"}`} />
          <Step n={2} active={step === 2} done={false} label="Richiesta preventivo" />
        </div>

        <div className="bg-white border border-blue-100 rounded-xl p-4 sm:p-6 shadow-md">
          {/* 🆕 Banner errori campi mancanti (visibile in cima a entrambi gli step) */}
          {formErrors.length > 0 && (
            <div className="mb-4 rounded-lg border-l-4 border-rose-500 bg-rose-50 p-3.5 shadow-sm" data-testid="pub-form-errors" role="alert">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-rose-800 mb-1">
                    Impossibile procedere — {formErrors.length} {formErrors.length === 1 ? "campo da compilare" : "campi da compilare"}
                  </div>
                  <ul className="text-xs text-rose-700 list-disc list-inside space-y-0.5" data-testid="pub-form-errors-list">
                    {formErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-rose-600 mt-2 italic">
                    Compila i campi indicati e riprova.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormErrors([])}
                  className="text-rose-500 hover:text-rose-700 p-1 rounded hover:bg-rose-100 transition-colors"
                  title="Chiudi"
                  data-testid="pub-form-errors-close"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <h2 className="font-display text-lg font-semibold mb-1">I tuoi dati anagrafici</h2>

              {/* 🆕 Selettore Persona fisica / Azienda */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wide text-blue-800 font-bold mb-2">Il preventivo è per:</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setClient({ ...client, client_kind: "person" })}
                    data-testid="pub-kind-person"
                    className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${client.client_kind === "person" ? "border-blue-600 bg-blue-600 text-white shadow-md" : "border-slate-300 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"}`}
                  >
                    👤 Persona fisica
                  </button>
                  <button
                    type="button"
                    onClick={() => setClient({ ...client, client_kind: "company" })}
                    data-testid="pub-kind-company"
                    className={`px-3 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${client.client_kind === "company" ? "border-indigo-600 bg-indigo-600 text-white shadow-md" : "border-slate-300 bg-white text-slate-700 hover:border-indigo-400 hover:text-indigo-700"}`}
                  >
                    🏢 Azienda / P.IVA
                  </button>
                </div>
              </div>

              <p className="text-xs text-slate-500 mb-2">
                {client.client_kind === "company"
                  ? <>Per un preventivo aziendale servono i dati della società e un referente. Per veicoli intestati a P.IVA <strong>non è applicabile</strong> la Legge Bersani.</>
                  : <>Per ricevere il preventivo bastano <strong>4 dati</strong>: nome, cognome, cellulare ed email. Gli altri campi sono <em>facoltativi</em> ma se li compili velocizzi la preparazione del preventivo.</>
                }
              </p>
              <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50/40 border border-amber-200 rounded-md px-2 py-1.5 mb-3 w-fit" data-testid="pub-required-legend">
                <span className="inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300"></span>
                {client.client_kind === "company" ? "I campi con sfondo giallo sono" : "I 4 campi con sfondo giallo sono"} <strong>obbligatori</strong>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* 🆕 === Dati Azienda (visibile solo se client_kind === company) === */}
                {client.client_kind === "company" && (
                  <div className="md:col-span-12 rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white p-3">
                    <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-2 flex items-center gap-1">
                      <span className="inline-block w-1 h-3 bg-indigo-600 rounded"></span> Dati Azienda
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div className="sm:col-span-2">
                        <Label>Ragione sociale <span className="text-red-500">*</span></Label>
                        <Input
                          data-testid="pub-business-name"
                          value={client.business_name || ""}
                          onChange={(e) => updateClient("business_name", (e.target.value || "").toUpperCase())}
                          placeholder="Es. ROSSI S.R.L."
                          className="uppercase bg-amber-50/40 border-amber-200 focus-visible:border-amber-400"
                        />
                      </div>
                      <div>
                        <Label>Partita IVA <span className="text-red-500">*</span></Label>
                        <Input
                          data-testid="pub-vat"
                          value={client.vat_number || ""}
                          onChange={(e) => updateClient("vat_number", (e.target.value || "").replace(/[^0-9]/g, "").slice(0, 11))}
                          placeholder="11 cifre"
                          inputMode="numeric"
                          maxLength={11}
                          className="font-mono tabular-nums bg-amber-50/40 border-amber-200 focus-visible:border-amber-400"
                        />
                        {(() => {
                          if (!(client.vat_number || "").trim()) return null;
                          const vv = validateItalianVAT(client.vat_number);
                          if (vv.ok) return <p className="text-[11px] text-emerald-700 mt-0.5" data-testid="pub-vat-check">✓ Partita IVA valida</p>;
                          if ((client.vat_number || "").length < 11) return <p className="text-[11px] text-slate-500 mt-0.5">Continua a digitare ({(client.vat_number || "").length}/11)…</p>;
                          return <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1 font-medium" data-testid="pub-vat-error">⚠️ {vv.message}</p>;
                        })()}
                      </div>
                      <div>
                        <Label>Codice Fiscale azienda</Label>
                        <Input
                          data-testid="pub-fc-company"
                          value={client.fiscal_code || ""}
                          onChange={(e) => updateClient("fiscal_code", e.target.value.toUpperCase())}
                          placeholder="Se diverso dalla P.IVA"
                          maxLength={16}
                          className="font-mono uppercase tracking-wide"
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck="false"
                        />
                        <p className="text-[10px] text-slate-500 mt-0.5">Per ditte individuali coincide con il CF del titolare.</p>
                      </div>
                      <div className="sm:col-span-2">
                        <Label>PEC (posta elettronica certificata)</Label>
                        <Input
                          data-testid="pub-pec"
                          type="email"
                          value={client.pec || ""}
                          onChange={(e) => updateClient("pec", (e.target.value || "").toLowerCase())}
                          placeholder="es. ragionesociale@pec.it"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* === Dati Referente / Persona fisica (col-span-7) === */}
                <div className="md:col-span-7 rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50/60 to-white p-3">
                  <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold mb-2 flex items-center gap-1">
                    <span className="inline-block w-1 h-3 bg-blue-600 rounded"></span>
                    {client.client_kind === "company" ? "Dati Referente" : "Dati personali"}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div><Label>Nome <span className="text-red-500">*</span>{Filled("name")}</Label><Input data-testid="pub-name" value={client.name} onChange={(e) => updateClient("name", (e.target.value || "").toUpperCase())} className={`uppercase ${autoFilledFields.has("name") ? "border-indigo-300 bg-indigo-50/40" : "bg-amber-50/40 border-amber-200 focus-visible:border-amber-400"}`} /></div>
                <div><Label>Cognome <span className="text-red-500">*</span>{Filled("surname")}</Label><Input data-testid="pub-surname" value={client.surname} onChange={(e) => updateClient("surname", (e.target.value || "").toUpperCase())} className={`uppercase ${autoFilledFields.has("surname") ? "border-indigo-300 bg-indigo-50/40" : "bg-amber-50/40 border-amber-200 focus-visible:border-amber-400"}`} /></div>
                <div>
                  <Label>
                    Data di nascita <span className="text-red-500">*</span>{Filled("birth_date")}
                    {(() => {
                      const bd = client.birth_date;
                      if (!bd || !/^\d{4}-\d{2}-\d{2}$/.test(bd)) return null;
                      const [y, m, d] = bd.split("-").map(Number);
                      const today = new Date();
                      let age = today.getFullYear() - y;
                      const md = (today.getMonth() + 1) - m;
                      if (md < 0 || (md === 0 && today.getDate() < d)) age -= 1;
                      if (age < 0 || age > 130) return null;
                      const isMinor = age < 18;
                      return (
                        <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded ${isMinor ? "bg-rose-100 text-rose-700 border border-rose-300" : "bg-emerald-100 text-emerald-700 border border-emerald-300"}`} data-testid="pub-bd-age">
                          {isMinor ? `⚠️ ${age} anni — minorenne` : `✓ ${age} anni`}
                        </span>
                      );
                    })()}
                  </Label>
                  <DatePicker
                    data-testid="pub-bd"
                    value={client.birth_date}
                    min="1900-01-01"
                    onChange={(e) => updateClient("birth_date", e.target.value)}
                    className={autoFilledFields.has("birth_date") ? "border-indigo-300 bg-indigo-50/40" : ""}
                  />
                  {/* 🆕 Avviso inline in tempo reale se data non valida (minorenne / futura / troppo antica) */}
                  {(() => {
                    const bd = client.birth_date;
                    if (!bd || !/^\d{4}-\d{2}-\d{2}$/.test(bd)) return null;
                    const [y, m, d] = bd.split("-").map(Number);
                    const bdDate = new Date(y, m - 1, d);
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    if (bdDate > today) {
                      return <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1 font-medium">⚠️ La data di nascita non può essere nel futuro.</p>;
                    }
                    if (y < 1900) {
                      return <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1 font-medium">⚠️ Data non valida (anno &lt; 1900).</p>;
                    }
                    let age = today.getFullYear() - y;
                    const md = (today.getMonth() + 1) - m;
                    if (md < 0 || (md === 0 && today.getDate() < d)) age -= 1;
                    if (age < 18) {
                      return <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1 font-medium" data-testid="pub-bd-warning">⚠️ Il conducente deve avere almeno <strong>18 anni compiuti</strong>. Età rilevata: {age} anni.</p>;
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <Label>Sesso <span className="text-red-500">*</span>{Filled("gender")}</Label>
                  <Select value={client.gender} onValueChange={(v) => updateClient("gender", v)}>
                    <SelectTrigger data-testid="pub-gender" className={autoFilledFields.has("gender") ? "border-indigo-300 bg-indigo-50/40" : ""}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">M</SelectItem>
                      <SelectItem value="F">F</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Luogo di nascita (comune) <span className="text-red-500">*</span>{Filled("birth_place")}</Label>
                  <CityAutocomplete
                    publicMode
                    testid="pub-birthplace"
                    value={client.birth_place}
                    onChange={(v) => updateClient("birth_place", (v || "").toUpperCase())}
                    onSelect={(c) => { updateClient("birth_place", (c.name || "").toUpperCase()); updateClient("belfiore_code", c.code || ""); }}
                    placeholder="Inizia a digitare il comune di nascita…"
                    inputClassName="uppercase"
                  />
                  {client.belfiore_code && (
                    <p className="text-[11px] text-emerald-700 mt-1">Codice catastale rilevato: <span className="font-mono font-semibold">{client.belfiore_code}</span></p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label>Codice Fiscale <span className="text-red-500">*</span>{Filled("fiscal_code")}</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      data-testid="pub-fc"
                      value={client.fiscal_code}
                      onChange={(e) => updateClient("fiscal_code", e.target.value.toUpperCase())}
                      className={`font-mono uppercase w-full sm:flex-1 tracking-wide ${autoFilledFields.has("fiscal_code") ? "border-indigo-300 bg-indigo-50/40" : ""}`}
                      placeholder="RSSMRA80A01H501Z"
                      maxLength={16}
                      inputMode="text"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck="false"
                    />
                    <Button type="button" data-testid="pub-calc-cf" variant="outline" onClick={tryAutoFiscalCode} className="w-full sm:w-auto">
                      <Sparkles className="w-4 h-4 mr-1" /> Calcola
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Puoi compilarlo manualmente o usare &quot;Calcola&quot; dopo aver inserito i dati anagrafici.</p>
                </div>
                <div>
                  <Label>Stato civile <span className="text-red-500">*</span></Label>
                  <Select value={client.marital_status || "__none__"} onValueChange={(v) => setClient({ ...client, marital_status: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="pub-marital"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Non specificato —</SelectItem>
                      <SelectItem value="celibe_nubile">Celibe / Nubile</SelectItem>
                      <SelectItem value="coniugato">Coniugato/a</SelectItem>
                      <SelectItem value="convivente">Convivente</SelectItem>
                      <SelectItem value="separato">Separato/a</SelectItem>
                      <SelectItem value="divorziato">Divorziato/a</SelectItem>
                      <SelectItem value="vedovo">Vedovo/a</SelectItem>
                      <SelectItem value="unito_civilmente">Unito/a civilmente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Professione (lookup dinamica per agenzia) */}
                <div>
                  <Label>
                    Professione <span className="text-red-500">*</span>
                    {professionLoading && <span className="text-[10px] text-slate-500 font-normal ml-1">(caricamento…)</span>}
                    {!professionLoading && professionOptions.length === 0 && <span className="text-[10px] text-amber-600 font-normal ml-1">(nessuna voce configurata)</span>}
                  </Label>
                  <Select
                    value={client.profession || "__none__"}
                    onValueChange={(v) => setClient({ ...client, profession: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="pub-profession"><SelectValue placeholder={professionLoading ? "Caricamento…" : "—"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Non specificata —</SelectItem>
                      {professionOptions.map((p) => (
                        <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                  </div>
                </div>

                {/* === Contatti (col-span-5) === */}
                <div className="md:col-span-5 rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white p-3">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-2 flex items-center gap-1">
                    <span className="inline-block w-1 h-3 bg-emerald-600 rounded"></span> Contatti
                  </div>
                  <div className="grid grid-cols-1 gap-2.5">
                <div>
                  <Label>Cellulare <span className="text-red-500">*</span>{Filled("mobile")}</Label>
                  {(() => {
                    const mv = client.mobile ? validateItalianMobile(client.mobile) : null;
                    const invalid = mv && !mv.ok;
                    const valid = mv && mv.ok;
                    return (
                      <>
                        <Input
                          data-testid="pub-mobile"
                          value={client.mobile}
                          onChange={(e) => updateClient("mobile", e.target.value)}
                          placeholder="Es. 3331234567"
                          className={
                            autoFilledFields.has("mobile") ? "border-indigo-300 bg-indigo-50/40" :
                            invalid ? "border-rose-500 bg-rose-50/30" :
                            valid ? "border-emerald-500 bg-emerald-50/30" :
                            "bg-amber-50/40 border-amber-200 focus-visible:border-amber-400"
                          }
                        />
                        {invalid && <span className="text-[10px] text-rose-600 mt-0.5 block">{mv.message}</span>}
                        {valid && <span className="text-[10px] text-emerald-700 mt-0.5 block">✓ Numero valido</span>}
                      </>
                    );
                  })()}
                </div>
                <div><Label>Telefono</Label><Input data-testid="pub-phone" value={client.phone} onChange={(e) => updateClient("phone", e.target.value)} placeholder="Opzionale" /></div>
                <div><Label>Email <span className="text-red-500">*</span>{Filled("email")}</Label><Input data-testid="pub-email" type="email" value={client.email} onChange={(e) => updateClient("email", e.target.value)} className={autoFilledFields.has("email") ? "border-indigo-300 bg-indigo-50/40" : "bg-amber-50/40 border-amber-200 focus-visible:border-amber-400"} /></div>
                  </div>
                </div>

                {/* === Residenza (col-span-12) === */}
                <div className="md:col-span-12 rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50/60 to-white p-3">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-2 flex items-center gap-1">
                    <span className="inline-block w-1 h-3 bg-amber-600 rounded"></span> Residenza
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="sm:col-span-2">
                  <Label>Città{Filled("city")}</Label>
                  <CityAutocomplete
                    publicMode
                    data-testid="pub-city"
                    value={client.city}
                    onChange={(v) => updateClient("city", (v || "").toUpperCase())}
                    onSelect={(c) => { updateClient("city", (c.name || "").toUpperCase()); if (c.province) updateClient("province", c.province); if (c.cap) updateClient("cap", c.cap); }}
                    inputClassName="uppercase"
                  />
                </div>
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2.5">
                  <div>
                    <Label>Indirizzo (con CAP automatico){Filled("address")}</Label>
                    <AddressAutocomplete
                      publicMode
                      data-testid="pub-address"
                      value={client.address}
                      onChange={(v) => updateClient("address", (v || "").toUpperCase())}
                      onSelect={(a) => { updateClient("address", (a.road || a.address || "").toUpperCase()); if (a.postcode) updateClient("cap", a.postcode); if (a.city) updateClient("city", (a.city || "").toUpperCase()); if (a.province) updateClient("province", a.province); if (a.house_number) updateClient("house_number", a.house_number); }}
                      city={client.city}
                      inputClassName="uppercase"
                    />
                  </div>
                  <div>
                    <Label>Civico{Filled("house_number")}</Label>
                    <Input
                      data-testid="pub-house-number"
                      value={client.house_number || ""}
                      onChange={(e) => updateClient("house_number", e.target.value.toUpperCase().slice(0, 10))}
                      placeholder="Es. 12/A"
                      maxLength={10}
                      className={`uppercase ${autoFilledFields.has("house_number") ? "border-indigo-300 bg-indigo-50/40" : ""}`}
                    />
                  </div>
                </div>
                <div>
                  <Label>CAP{Filled("cap")}</Label>
                  <div className="relative">
                    <Input
                      data-testid="pub-cap"
                      value={client.cap}
                      onChange={(e) => updateClient("cap", e.target.value.replace(/\D/g, "").slice(0, 5))}
                      className={`font-mono ${autoFilledFields.has("cap") ? "border-indigo-300 bg-indigo-50/40" : ""}`}
                      maxLength={5}
                      placeholder="00100"
                    />
                    {capLookupLoading && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-amber-500" />}
                    {capSuggestions.length > 1 && (
                      <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-amber-200 rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid="pub-cap-suggestions">
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border-b border-amber-100 font-semibold">
                          Più comuni hanno il CAP {client.cap}. Scegli:
                        </div>
                        {capSuggestions.map((s) => (
                          <button
                            key={`${s.name}-${s.province}`}
                            type="button"
                            data-testid={`pub-cap-pick-${s.province}`}
                            onClick={() => { updateClient("city", (s.name || "").toUpperCase()); updateClient("province", s.province); setCapSuggestions([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 flex items-center gap-2"
                          >
                            <span className="flex-1 truncate">{s.name}</span>
                            <span className="text-xs font-mono bg-slate-100 rounded px-1.5 py-0.5">{s.province}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div><Label>Provincia{Filled("province")}</Label><Input data-testid="pub-province" value={client.province} onChange={(e) => updateClient("province", e.target.value.toUpperCase())} className={`uppercase ${autoFilledFields.has("province") ? "border-indigo-300 bg-indigo-50/40" : ""}`} maxLength={2} placeholder="Es. RM" /></div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button data-testid="pub-next" onClick={() => { if (validateStep1()) setStep(2); }} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md">
                  Avanti <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display text-lg font-semibold mb-2">Cosa vuoi assicurare?</h2>
              <p className="text-sm text-slate-500 mb-4">Seleziona il tipo di polizza e indica le coperture desiderate. I campi obbligatori sono contrassegnati con <span className="text-red-500">*</span>.</p>
              <div>
                <Label>Tipo polizza <span className="text-red-500">*</span></Label>
                <Select value={quote.type} onValueChange={(v) => setQuote({ ...quote, type: v, details: {} })}>
                  <SelectTrigger data-testid="pub-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* 🆕 Situazione assicurativa — appare subito dopo il tipo polizza (OBBLIGATORIA) */}
              {insuranceStatusOptions.length > 0 && (
                <div>
                  <Label>Situazione assicurativa <span className="text-red-500">*</span> <span className="text-[10px] font-normal text-slate-500">(stato del veicolo)</span></Label>
                  <Select
                    value={quote.details?.insurance_status || ""}
                    onValueChange={(v) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), insurance_status: v } }))}
                  >
                    <SelectTrigger data-testid="pub-insurance-status" className={!quote.details?.insurance_status ? "bg-amber-50/40 border-amber-200 focus-visible:border-amber-400" : ""}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                    <SelectContent>
                      {insuranceStatusOptions.map((o) => (
                        <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* 🆕 Feb 2026 — Dropdown "Attestato di rischio altro veicolo?" visibile SOLO se la voce `insurance_status`
                  selezionata ha flag `enables_other_attestato=true` (configurabile in Impostazioni > Liste Preventivo > Situazione Assicurativa). */}
              {quote.type === "auto" && bersaniSourceOptions.length > 0 && !!insuranceStatusOptions.find((o) => o.code === quote.details?.insurance_status)?.enables_other_attestato && (
                <div data-testid="pub-bersani-source-wrap">
                  <Label className="text-xs font-medium text-slate-700">
                    Vuoi utilizzare l&apos;attestato di rischio di un altro veicolo? <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={quote.details?.bersani_source || ""}
                    onValueChange={(v) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), bersani_source: v } }))}
                  >
                    <SelectTrigger
                      data-testid="pub-bersani-source"
                      className={!quote.details?.bersani_source ? "bg-amber-50/40 border-amber-200 focus-visible:border-amber-400" : ""}
                    >
                      <SelectValue placeholder="Seleziona…" />
                    </SelectTrigger>
                    <SelectContent>
                      {bersaniSourceOptions.map((o) => (
                        <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!quote.details?.bersani_source && <p className="text-[10px] text-amber-700 font-medium mt-0.5">⚠ Campo obbligatorio</p>}
                </div>
              )}

              {/* 🆕 Mini-form "Dati veicolo di origine" quando la voce `bersani_source` richiede targa altro veicolo */}
              {quote.type === "auto" && !!bersaniSourceOptions.find((o) => o.code === quote.details?.bersani_source)?.requires_other_plate && (
                <div data-testid="pub-other-vehicle-box" className="rounded-lg border-2 border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50/40 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sky-800 font-semibold text-sm">
                    🚘 Dati veicolo di origine
                    <span className="text-[10px] font-normal text-sky-700">({(bersaniSourceOptions.find((o) => o.code === quote.details?.bersani_source)?.label) || ""})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Targa veicolo di origine *</Label>
                      <Input value={quote.details?.other_vehicle_plate || ""} onChange={(e) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), other_vehicle_plate: (e.target.value || "").toUpperCase() } }))} placeholder="es. AB123CD" maxLength={10} className="h-9 text-sm font-mono uppercase" data-testid="pub-other-vehicle-plate" />
                    </div>
                    <div>
                      <Label className="text-xs">Marca</Label>
                      <Input value={quote.details?.other_vehicle_brand || ""} onChange={(e) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), other_vehicle_brand: e.target.value } }))} placeholder="es. Fiat" className="h-9 text-sm" data-testid="pub-other-vehicle-brand" />
                    </div>
                    <div>
                      <Label className="text-xs">Modello</Label>
                      <Input value={quote.details?.other_vehicle_model || ""} onChange={(e) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), other_vehicle_model: e.target.value } }))} placeholder="es. Panda" className="h-9 text-sm" data-testid="pub-other-vehicle-model" />
                    </div>
                    <div>
                      <Label className="text-xs">Anno 1ª immatricolazione</Label>
                      <Input type="date" value={quote.details?.other_vehicle_first_registration || ""} onChange={(e) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), other_vehicle_first_registration: e.target.value } }))} className="h-9 text-sm" data-testid="pub-other-vehicle-first-reg" />
                    </div>
                    <div className="sm:col-span-4">
                      <Label className="text-xs">Note aggiuntive</Label>
                      <Input value={quote.details?.other_vehicle_notes || ""} onChange={(e) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), other_vehicle_notes: e.target.value } }))} placeholder="es. Data cessione, demolizione, denuncia furto…" className="h-9 text-sm" data-testid="pub-other-vehicle-notes" />
                    </div>
                  </div>
                </div>
              )}

              {/* 🆕 Feb 2026 — Sezione LEGGE BERSANI / RC FAMILIARE — visibile se la voce `bersani_source` apre la sezione (opens_bersani_section)
                  OPPURE (retrocompat) se la Situazione Assicurativa ha `triggers_bersani=true` e nessun bersani_source è stato selezionato. */}
              {quote.type === "auto" && (
                !!bersaniSourceOptions.find((o) => o.code === quote.details?.bersani_source)?.opens_bersani_section
                || (!quote.details?.bersani_source && !!insuranceStatusOptions.find((o) => o.code === quote.details?.insurance_status)?.triggers_bersani)
              ) && (() => {
                const KINSHIP_OPTIONS = [
                  { code: "coniuge", label: "Coniuge" },
                  { code: "convivente", label: "Convivente" },
                  { code: "figlio", label: "Figlio/a" },
                  { code: "genitore", label: "Genitore" },
                  { code: "fratello", label: "Fratello/Sorella" },
                  { code: "altro", label: "Altro" },
                ];
                const MERIT_OPTIONS = Array.from({ length: 18 }, (_, i) => String(i + 1));
                const setD = (patch) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), ...patch } }));
                const bersaniOn = !!quote.details?.bersani_applicable;
                const rcFamOn = !!quote.details?.rc_familiare_applicable;
                const bersaniAtt = attachments.find((a) => a.id === bersaniLibrettoId);
                const rcFamAtt = attachments.find((a) => a.id === rcFamLibrettoId);
                const handleUploadBersani = async (file) => {
                  if (!file) return;
                  const _plate = (quote.details?.bersani_ref_plate || "").trim().toUpperCase();
                  const enriched = await uploadAttachment(file, `Libretto veicolo Bersani${_plate ? ` (targa ${_plate})` : ""}`);
                  if (enriched?.id) setBersaniLibrettoId(enriched.id);
                };
                const handleUploadRcFam = async (file) => {
                  if (!file) return;
                  const _plate = (quote.details?.rc_familiare_plate || "").trim().toUpperCase();
                  const enriched = await uploadAttachment(file, `Libretto veicolo RC Familiare${_plate ? ` (targa ${_plate})` : ""}`);
                  if (enriched?.id) setRcFamLibrettoId(enriched.id);
                };
                const removeBersaniLibretto = () => { if (bersaniLibrettoId) { removeAttachment(bersaniLibrettoId); setBersaniLibrettoId(null); } };
                const removeRcFamLibretto = () => { if (rcFamLibrettoId) { removeAttachment(rcFamLibrettoId); setRcFamLibrettoId(null); } };

                return (
                  <div className="rounded-lg border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-white to-orange-50/40 p-4 space-y-4" data-testid="pub-bersani-section">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-rose-600" />
                      <p className="text-xs uppercase tracking-wide text-rose-700 font-bold">Legge Bersani / RC Familiare</p>
                    </div>
                    <p className="text-[11px] text-slate-600">
                      Se hai un veicolo già assicurato in famiglia o un familiare convivente con una polizza attiva, puoi ereditare la sua classe di merito.
                    </p>

                    {/* ================= LEGGE BERSANI ================= */}
                    <div className="rounded-md border border-rose-200 bg-white p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">📜 Legge Bersani</p>
                          <p className="text-[11px] text-slate-500">Erediti la classe di un veicolo già assicurato nel tuo nucleo familiare.</p>
                        </div>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bersaniOn}
                            onChange={(e) => setD({ bersani_applicable: e.target.checked })}
                            className="h-4 w-4 accent-rose-600"
                            data-testid="pub-bersani-toggle"
                          />
                          <span className={`text-xs font-semibold ${bersaniOn ? "text-rose-700" : "text-slate-500"}`}>
                            {bersaniOn ? "Sì" : "No"}
                          </span>
                        </label>
                      </div>
                      {bersaniOn && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Targa veicolo di riferimento <span className="text-red-500">*</span></Label>
                            <Input
                              data-testid="pub-bersani-ref-plate"
                              value={quote.details?.bersani_ref_plate || ""}
                              onChange={(e) => setD({ bersani_ref_plate: (e.target.value || "").toUpperCase() })}
                              placeholder="es. AB123CD"
                              className="uppercase font-mono"
                              maxLength={10}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Intestatario (nome e cognome) <span className="text-red-500">*</span></Label>
                            <Input
                              data-testid="pub-bersani-ref-owner"
                              value={quote.details?.bersani_ref_owner || ""}
                              onChange={(e) => setD({ bersani_ref_owner: e.target.value })}
                              placeholder="es. Mario Rossi"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Grado di parentela <span className="text-red-500">*</span></Label>
                            <Select
                              value={quote.details?.bersani_ref_kinship || ""}
                              onValueChange={(v) => setD({ bersani_ref_kinship: v })}
                            >
                              <SelectTrigger data-testid="pub-bersani-ref-kinship"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                              <SelectContent>
                                {KINSHIP_OPTIONS.map((k) => (
                                  <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Compagnia assicurazione veicolo</Label>
                            <Input
                              data-testid="pub-bersani-ref-company"
                              value={quote.details?.bersani_ref_company || ""}
                              onChange={(e) => setD({ bersani_ref_company: e.target.value })}
                              placeholder="es. Unipol, Generali…"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Classe di merito attuale <span className="text-red-500">*</span></Label>
                            <Select
                              value={quote.details?.bersani_ref_merit_class || ""}
                              onValueChange={(v) => setD({ bersani_ref_merit_class: v })}
                            >
                              <SelectTrigger data-testid="pub-bersani-ref-merit"><SelectValue placeholder="Classe 1–18" /></SelectTrigger>
                              <SelectContent>
                                {MERIT_OPTIONS.map((m) => (
                                  <SelectItem key={m} value={m}>Classe {m}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-xs">Allega libretto veicolo di riferimento <span className="text-[10px] font-normal text-slate-500">(PDF o immagine, max 10 MB)</span></Label>
                            {bersaniAtt ? (
                              <div className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50/50 px-3 py-2 mt-1" data-testid="pub-bersani-libretto-preview">
                                <FileText className="h-4 w-4 text-emerald-700 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium truncate">{bersaniAtt.filename}</div>
                                  <div className="text-[10px] text-slate-500">{Math.round((bersaniAtt.size || 0) / 1024)} KB</div>
                                </div>
                                <button type="button" onClick={removeBersaniLibretto} className="text-xs text-rose-700 hover:underline font-semibold" data-testid="pub-bersani-libretto-remove">Rimuovi</button>
                              </div>
                            ) : (
                              <label className="mt-1 flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-rose-300 bg-white/60 px-4 py-4 cursor-pointer hover:border-rose-500 hover:bg-rose-50/40 transition text-center">
                                <Upload className="h-4 w-4 text-rose-700" />
                                <span className="text-xs font-medium text-rose-800">Clicca per allegare il libretto</span>
                                <span className="text-[10px] text-slate-500">PDF, JPG, PNG, WEBP · max 10 MB</span>
                                <input
                                  type="file"
                                  accept="application/pdf,image/*"
                                  className="hidden"
                                  disabled={uploadingAtt}
                                  data-testid="pub-bersani-libretto-input"
                                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleUploadBersani(f); }}
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ================= RC FAMILIARE ================= */}
                    <div className="rounded-md border border-rose-200 bg-white p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">👨‍👩‍👧 RC Familiare</p>
                          <p className="text-[11px] text-slate-500">Erediti la classe di un familiare convivente con polizza attiva.</p>
                        </div>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rcFamOn}
                            onChange={(e) => setD({ rc_familiare_applicable: e.target.checked })}
                            className="h-4 w-4 accent-rose-600"
                            data-testid="pub-rcfam-toggle"
                          />
                          <span className={`text-xs font-semibold ${rcFamOn ? "text-rose-700" : "text-slate-500"}`}>
                            {rcFamOn ? "Sì" : "No"}
                          </span>
                        </label>
                      </div>
                      {rcFamOn && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Familiare convivente (nome e cognome) <span className="text-red-500">*</span></Label>
                            <Input
                              data-testid="pub-rcfam-owner"
                              value={quote.details?.rc_familiare_owner || ""}
                              onChange={(e) => setD({ rc_familiare_owner: e.target.value })}
                              placeholder="es. Anna Rossi"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Grado di parentela <span className="text-red-500">*</span></Label>
                            <Select
                              value={quote.details?.rc_familiare_kinship || ""}
                              onValueChange={(v) => setD({ rc_familiare_kinship: v })}
                            >
                              <SelectTrigger data-testid="pub-rcfam-kinship"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                              <SelectContent>
                                {KINSHIP_OPTIONS.map((k) => (
                                  <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Targa veicolo familiare <span className="text-red-500">*</span></Label>
                            <Input
                              data-testid="pub-rcfam-plate"
                              value={quote.details?.rc_familiare_plate || ""}
                              onChange={(e) => setD({ rc_familiare_plate: (e.target.value || "").toUpperCase() })}
                              placeholder="es. AB123CD"
                              className="uppercase font-mono"
                              maxLength={10}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Classe di merito del familiare <span className="text-red-500">*</span></Label>
                            <Select
                              value={quote.details?.rc_familiare_merit_class || ""}
                              onValueChange={(v) => setD({ rc_familiare_merit_class: v })}
                            >
                              <SelectTrigger data-testid="pub-rcfam-merit"><SelectValue placeholder="Classe 1–18" /></SelectTrigger>
                              <SelectContent>
                                {MERIT_OPTIONS.map((m) => (
                                  <SelectItem key={m} value={m}>Classe {m}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-xs">Allega libretto veicolo familiare <span className="text-[10px] font-normal text-slate-500">(PDF o immagine, max 10 MB)</span></Label>
                            {rcFamAtt ? (
                              <div className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50/50 px-3 py-2 mt-1" data-testid="pub-rcfam-libretto-preview">
                                <FileText className="h-4 w-4 text-emerald-700 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium truncate">{rcFamAtt.filename}</div>
                                  <div className="text-[10px] text-slate-500">{Math.round((rcFamAtt.size || 0) / 1024)} KB</div>
                                </div>
                                <button type="button" onClick={removeRcFamLibretto} className="text-xs text-rose-700 hover:underline font-semibold" data-testid="pub-rcfam-libretto-remove">Rimuovi</button>
                              </div>
                            ) : (
                              <label className="mt-1 flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-rose-300 bg-white/60 px-4 py-4 cursor-pointer hover:border-rose-500 hover:bg-rose-50/40 transition text-center">
                                <Upload className="h-4 w-4 text-rose-700" />
                                <span className="text-xs font-medium text-rose-800">Clicca per allegare il libretto</span>
                                <span className="text-[10px] text-slate-500">PDF, JPG, PNG, WEBP · max 10 MB</span>
                                <input
                                  type="file"
                                  accept="application/pdf,image/*"
                                  className="hidden"
                                  disabled={uploadingAtt}
                                  data-testid="pub-rcfam-libretto-input"
                                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleUploadRcFam(f); }}
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Drag & Drop libretto: estrazione AI per auto/moto */}
              {(quote.type === "auto" || quote.type === "moto") && (
                <div className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50/40 p-3" data-testid="pub-libretto-block">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-blue-600" />
                    <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Compilazione automatica da libretto</p>
                  </div>
                  <p className="text-[11px] text-slate-600 mb-2">
                    Trascina una foto/PDF del libretto: i dati del veicolo e dell&apos;intestatario verranno compilati automaticamente.
                  </p>
                  <div className="flex gap-2 mb-2">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        disabled={extracting}
                        data-testid="pub-libretto-camera"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) extractFromLibretto(f); e.target.value = ""; }}
                      />
                      <div className="flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                        📷 Scatta foto libretto
                      </div>
                    </label>
                  </div>
                  <label className={`block cursor-pointer ${extracting ? "pointer-events-none" : ""}`}>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      disabled={extracting}
                      data-testid="pub-libretto-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) extractFromLibretto(f);
                        e.target.value = "";
                      }}
                    />                    <DropZone
                      testid="pub-libretto-drop"
                      accept={["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]}
                      multiple={false}
                      disabled={extracting}
                      onFiles={(files) => { if (files?.[0]) extractFromLibretto(files[0]); }}
                      className={`rounded-md border-2 border-dashed transition-colors ${
                        extractedSummary ? "border-emerald-400 bg-emerald-50/40"
                          : extractionError ? "border-rose-400 bg-rose-50/40"
                          : "border-blue-300 bg-white/60"
                      }`}
                    >
                      <div className={`flex flex-col items-center justify-center py-4 px-3 text-center ${extracting ? "opacity-60" : ""}`}>
                        {extracting ? (
                          <>
                            <Loader2 className="h-5 w-5 text-blue-600 animate-spin mb-1" />
                            <p className="text-xs font-medium text-blue-700">Estrazione in corso…</p>
                          </>
                        ) : extractedSummary ? (
                          <>
                            <CheckCircle2 className="h-5 w-5 text-emerald-600 mb-1" />
                            <p className="text-xs font-semibold text-emerald-700">
                              Dati estratti con successo
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-1.5 justify-center">
                              {extractedSummary.vehicleCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 font-semibold">
                                  🚗 {extractedSummary.vehicleCount} campi veicolo
                                </span>
                              )}
                              {extractedSummary.ownerCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold">
                                  👤 {extractedSummary.ownerCount} campi intestatario
                                </span>
                              )}
                              {extractedSummary.extrasCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 font-semibold">
                                  ➕ {extractedSummary.extrasCount} dati extra
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1.5">Carica un altro file per ri-estrarre</p>
                          </>
                        ) : extractionError ? (
                          <>
                            <AlertCircle className="h-5 w-5 text-rose-600 mb-1" />
                            <p className="text-xs font-medium text-rose-700">{extractionError}</p>
                            <p className="text-[10px] text-slate-500 mt-1">Riprova con un&apos;altra foto o PDF</p>
                          </>
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-blue-600 mb-1" />
                            <p className="text-xs font-medium text-blue-700">Trascina qui il libretto</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">o clicca per selezionare un file (JPG, PNG, PDF, HEIC)</p>
                          </>
                        )}
                      </div>
                    </DropZone>
                  </label>
                  {extractedSummary && extractedSummary.ownerCount > 0 && (
                    <div className="mt-2 text-[10px] text-slate-600 bg-emerald-50/60 border border-emerald-200 rounded px-2 py-1.5">
                      ✓ I dati dell&apos;intestatario sono stati copiati nel passo 1 (solo nei campi vuoti).
                    </div>
                  )}
                </div>
              )}

              {/* === Sezione Allegati (PDF, immagini) === */}
              <div className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/40 p-3" data-testid="pub-attachments-block">
                <div className="flex items-center gap-2 mb-2">
                  <Upload className="h-4 w-4 text-violet-600" />
                  <p className="text-xs uppercase tracking-wide text-violet-700 font-semibold">📎 Allegati</p>
                </div>
                {requiredDocs.length > 0 && (
                  <div className="mb-2 bg-white/70 border border-violet-100 rounded px-2.5 py-2">
                    <p className="text-[10px] uppercase font-semibold text-violet-700 mb-1">Documenti richiesti per polizza {typeLabel(quote.type)}:</p>
                    <ul className="space-y-0.5">
                      {requiredDocs.map((d) => {
                        // 🆕 Match euristico: un doc è "spuntato" se esiste un allegato con description/filename che contiene il nome del doc.
                        const needle = (d.name || "").toLowerCase();
                        const matched = attachments.find((a) => {
                          const hay = `${(a.description || "").toLowerCase()} ${(a.filename || "").toLowerCase()}`;
                          return needle && (hay.includes(needle) || (needle.includes("libretto") && hay.includes("libretto")) || (needle.includes("patente") && hay.includes("patente")) || (needle.includes("carta") && needle.includes("identit") && hay.includes("identit")));
                        });
                        return (
                          <li key={d.id} className={`text-[11px] flex items-start gap-1 ${matched ? "text-emerald-700" : "text-slate-700"}`} data-testid={`pub-required-doc-${d.id}`}>
                            <span className={matched ? "text-emerald-600 font-bold" : (d.mandatory ? "text-rose-600 font-bold" : "text-slate-400")}>
                              {matched ? "✓" : (d.mandatory ? "★" : "○")}
                            </span>
                            <span>
                              <strong className={matched ? "line-through" : ""}>{d.name}</strong>
                              {d.mandatory && !matched && <span className="text-rose-600 text-[10px]"> (obbligatorio)</span>}
                              {matched && <span className="text-emerald-700 text-[10px] font-semibold ml-1">✓ allegato</span>}
                              {d.description && <span className="text-slate-500"> — {d.description}</span>}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2 mb-2">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      className="hidden"
                      disabled={uploadingAtt}
                      data-testid="pub-att-input"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach((f) => uploadAttachment(f));
                        e.target.value = "";
                      }}
                    />
                    <div className="flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white transition-colors">
                      📁 Carica file (PDF/immagini)
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      disabled={uploadingAtt}
                      data-testid="pub-att-camera"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ""; }}
                    />
                    <div className="flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white transition-colors">
                      📷 Scatta foto
                    </div>
                  </label>
                </div>
                {uploadingAtt && <p className="text-[11px] text-violet-700 animate-pulse">Caricamento in corso…</p>}
                {attachments.length > 0 && (
                  <ul className="space-y-1 mt-2" data-testid="pub-att-list">
                    {attachments.map((a) => (
                      <li key={a.id} className="flex items-center justify-between text-xs bg-white border border-violet-100 rounded px-2.5 py-1.5">
                        <span className="truncate text-slate-700">
                          {(a.content_type || "").includes("pdf") ? "📄" : "🖼️"} <strong>{a.filename}</strong>
                          <span className="text-slate-400 ml-1">({Math.round(a.size / 1024)} KB)</span>
                          {a.description && <span className="ml-1.5 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 uppercase tracking-wide">{a.description}</span>}
                        </span>
                        <button type="button" onClick={() => removeAttachment(a.id)} className="text-rose-600 hover:text-rose-800 text-[10px] font-semibold ml-2">
                          Rimuovi
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-slate-500 mt-2 italic">Massimo 10MB per file. PDF, JPG, PNG, HEIC supportati.</p>
              </div>

              <div className="border-t border-slate-200 pt-3">
                <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-2">Dati specifici {typeLabel(quote.type)}</p>
                <QuoteDetailsForm
                  type={quote.type}
                  details={quote.details}
                  onChange={(newDetails) => setQuote({ ...quote, details: newDetails })}
                  requirePlate={quote.type === "auto" || quote.type === "moto"}
                  publicMode
                  publicKinshipDegrees={kinshipDegrees}
                  publicAnnualMileage={annualMileageOptions}
                  publicAntitheft={antitheftOptions}
                  publicEducation={educationOptions}
                  publicCurrentCompanies={currentCompanyOptions}
                  publicClientKind={client.client_kind}
                />
              </div>
              <div>
                <Label>Garanzie di interesse <span className="text-[10px] font-normal text-slate-500">(spunta le coperture che ti interessano)</span></Label>
                <CoveragePicker
                  publicMode
                  testid="pub-coverages"
                  policyType={quote.type}
                  value={quote.details?.selected_coverages || []}
                  onChange={(codes, labels) => {
                    const composedText = labels.join(" · ");
                    setQuote((q) => {
                      const prevAuto = q.details?._coverages_auto_text || "";
                      const currentText = (q.coverage_request || "").trim();
                      // Se vuoto o uguale alla precedente composizione automatica, sovrascrive
                      const nextText = (!currentText || currentText === prevAuto) ? composedText : currentText;
                      return {
                        ...q,
                        details: { ...(q.details || {}), selected_coverages: codes, _coverages_auto_text: composedText },
                        coverage_request: nextText,
                      };
                    });
                  }}
                />
              </div>
              <div>
                <Label>Frazionamento preferito <span className="text-[10px] font-normal text-slate-500">(periodicità rate)</span></Label>
                <Select
                  value={quote.details?.frazionamento || "annuale"}
                  onValueChange={(v) => setQuote((q) => ({ ...q, details: { ...(q.details || {}), frazionamento: v } }))}
                >
                  <SelectTrigger data-testid="pub-frazionamento" className="bg-amber-50/40 border-amber-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annuale">Annuale (1 rata/anno)</SelectItem>
                    <SelectItem value="semestrale">Semestrale (2 rate/anno)</SelectItem>
                    <SelectItem value="quadrimestrale">Quadrimestrale (3 rate/anno)</SelectItem>
                    <SelectItem value="trimestrale">Trimestrale (4 rate/anno)</SelectItem>
                    <SelectItem value="mensile">Mensile (12 rate/anno)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Richiesta copertura / Note aggiuntive <span className="text-red-500">*</span></Label>
                <Textarea data-testid="pub-coverage" rows={3} value={quote.coverage_request} onChange={(e) => setQuote({ ...quote, coverage_request: e.target.value })} placeholder="Aggiungi dettagli: uso del veicolo, garanzie specifiche…" className="bg-amber-50/40 border-amber-200 focus-visible:border-amber-400" />
              </div>
              <div className="flex justify-between pt-4">
                <Button data-testid="pub-back" variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
                </Button>
                <Button data-testid="pub-submit" onClick={openReview} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                  <Sparkles className="w-4 h-4 mr-2" /> {submitting ? "Invio…" : "Rivedi e invia"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          I tuoi dati saranno trattati secondo la nostra Privacy Policy e usati esclusivamente per fornire il preventivo richiesto.
        </p>
        <p className="text-center text-[11px] text-slate-400 mt-3" data-testid="credits">
          Software realizzato by <span className="font-semibold text-slate-600">Vincenzo Amoroso</span>
        </p>
      </div>

      {/* Dialog di riepilogo prima dell'invio */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="pub-review-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              Conferma la tua richiesta
            </DialogTitle>
            <p className="text-sm text-slate-600 mt-1">
              Controlla che i dati siano corretti. Se vuoi modificare qualcosa, chiudi il riepilogo e premi <strong className="text-slate-800">&quot;Modifica&quot;</strong>.
            </p>
          </DialogHeader>

          <div className="px-5 py-4 space-y-3">
            {/* 1. Dati personali (BLU) */}
            <section className="rounded-lg border-l-4 border-blue-500 bg-blue-50/50 border border-blue-100 overflow-hidden">
              <div className="px-3 py-2 bg-blue-100/60 border-b border-blue-200 flex items-center gap-2">
                <User className="w-4 h-4 text-blue-700" />
                <div className="text-xs uppercase font-bold tracking-wider text-blue-800">Dati personali</div>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm p-3">
                <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Nome completo</dt><dd className="font-semibold">{client.name || "—"} {client.surname || ""}</dd></div>
                <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Codice fiscale</dt><dd className="font-mono text-xs">{client.fiscal_code || "—"}</dd></div>
                <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Data di nascita</dt><dd>{formatDetailValue(client.birth_date)}</dd></div>
                <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Sesso</dt><dd>{client.gender === "F" ? "Femmina" : client.gender === "M" ? "Maschio" : "—"}</dd></div>
                <div className="sm:col-span-2"><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Luogo di nascita</dt><dd>{client.birth_place || "—"}</dd></div>
                {client.marital_status && (
                  <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Stato civile</dt><dd>{formatDetailValue(client.marital_status)}</dd></div>
                )}
                {client.profession && (
                  <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Professione</dt><dd>{(professionOptions.find((p) => p.code === client.profession) || {}).label || client.profession}</dd></div>
                )}
              </dl>
            </section>

            {/* 2. Contatti (VERDE) */}
            <section className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50/50 border border-emerald-100 overflow-hidden">
              <div className="px-3 py-2 bg-emerald-100/60 border-b border-emerald-200 flex items-center gap-2">
                <Phone className="w-4 h-4 text-emerald-700" />
                <div className="text-xs uppercase font-bold tracking-wider text-emerald-800">Contatti</div>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm p-3">
                <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Email</dt><dd className="break-all font-medium">{client.email || "—"}</dd></div>
                <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Cellulare</dt><dd className="font-medium">{client.mobile || "—"}</dd></div>
                {client.phone && <div className="sm:col-span-2"><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Telefono fisso</dt><dd>{client.phone}</dd></div>}
              </dl>
            </section>

            {/* 3. Residenza (AMBRA) */}
            {(client.address || client.city || client.cap) && (
              <section className="rounded-lg border-l-4 border-amber-500 bg-amber-50/50 border border-amber-100 overflow-hidden">
                <div className="px-3 py-2 bg-amber-100/60 border-b border-amber-200 flex items-center gap-2">
                  <MapPinIcon className="w-4 h-4 text-amber-700" />
                  <div className="text-xs uppercase font-bold tracking-wider text-amber-800">Residenza</div>
                </div>
                <p className="text-sm p-3 font-medium">
                  {[client.address && client.house_number ? `${client.address}, ${client.house_number}` : client.address, client.cap, client.city, client.province].filter(Boolean).join(", ") || "—"}
                </p>
              </section>
            )}

            {/* 4. Richiesta polizza (VIOLA) */}
            <section className="rounded-lg border-l-4 border-violet-500 bg-violet-50/50 border border-violet-100 overflow-hidden">
              <div className="px-3 py-2 bg-violet-100/60 border-b border-violet-200 flex items-center gap-2">
                <FileText className="w-4 h-4 text-violet-700" />
                <div className="text-xs uppercase font-bold tracking-wider text-violet-800">Richiesta preventivo</div>
              </div>
              <dl className="space-y-2 text-sm p-3">
                <div><dt className="text-slate-500 text-[11px] uppercase tracking-wide">Tipo polizza</dt><dd className="font-bold text-violet-900 text-base">{typeLabel(quote.type)}</dd></div>
                {Array.isArray(quote.details?.selected_coverages) && quote.details.selected_coverages.length > 0 && (
                  <div>
                    <dt className="text-slate-500 text-[11px] uppercase tracking-wide mb-1">Garanzie selezionate</dt>
                    <dd className="flex flex-wrap gap-1">
                      {quote.details.selected_coverages.map((c) => (
                        <span key={c} className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border border-violet-300 bg-white text-violet-700">{c.replace(/_/g, " ")}</span>
                      ))}
                    </dd>
                  </div>
                )}
                {quote.coverage_request && (
                  <div>
                    <dt className="text-slate-500 text-[11px] uppercase tracking-wide">Note / Richiesta copertura</dt>
                    <dd className="bg-white border border-violet-200 rounded p-2 mt-1 whitespace-pre-wrap text-sm">{quote.coverage_request}</dd>
                  </div>
                )}
              </dl>
            </section>

            {/* 5. Dettagli specifici → raggruppati per sezione con colore proprio */}
            {groupDetails(quote.details).map((g) => {
              const colorClasses = {
                sky: { border: "border-sky-500", bg: "bg-sky-50/50", border2: "border-sky-100", head: "bg-sky-100/60 border-sky-200", head_txt: "text-sky-800" },
                amber: { border: "border-orange-500", bg: "bg-orange-50/50", border2: "border-orange-100", head: "bg-orange-100/60 border-orange-200", head_txt: "text-orange-800" },
                teal: { border: "border-teal-500", bg: "bg-teal-50/50", border2: "border-teal-100", head: "bg-teal-100/60 border-teal-200", head_txt: "text-teal-800" },
                blue: { border: "border-indigo-500", bg: "bg-indigo-50/50", border2: "border-indigo-100", head: "bg-indigo-100/60 border-indigo-200", head_txt: "text-indigo-800" },
                slate: { border: "border-slate-500", bg: "bg-slate-50/50", border2: "border-slate-200", head: "bg-slate-100/60 border-slate-200", head_txt: "text-slate-800" },
              }[g.color] || {};
              return (
                <section key={g.title} className={`rounded-lg border-l-4 ${colorClasses.border} ${colorClasses.bg} border ${colorClasses.border2} overflow-hidden`}>
                  <div className={`px-3 py-2 ${colorClasses.head} border-b flex items-center gap-2`}>
                    <div className={`text-xs uppercase font-bold tracking-wider ${colorClasses.head_txt}`}>{g.title}</div>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm p-3">
                    {g.items.map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-slate-500 text-[11px] uppercase tracking-wide">{humanizeKey(k)}</dt>
                        <dd className="font-medium text-slate-800 break-words">{formatDetailValue(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              );
            })}

            {/* 6. Allegati (INDACO) */}
            {attachments.length > 0 && (
              <section className="rounded-lg border-l-4 border-indigo-500 bg-indigo-50/50 border border-indigo-100 overflow-hidden" data-testid="pub-review-attachments">
                <div className="px-3 py-2 bg-indigo-100/60 border-b border-indigo-200 flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-indigo-700" />
                  <div className="text-xs uppercase font-bold tracking-wider text-indigo-800">Documenti allegati ({attachments.length})</div>
                </div>
                <ul className="space-y-1.5 text-sm p-3">
                  {attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 bg-white/70 rounded px-2 py-1.5 border border-indigo-100">
                      <span className="text-lg leading-none flex-shrink-0">{(a.content_type || "").includes("pdf") ? "📄" : "🖼️"}</span>
                      <div className="flex-1 min-w-0">
                        {a.description && (
                          <div className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide truncate">
                            {a.description}
                          </div>
                        )}
                        <div className="truncate font-medium text-slate-800">{a.filename}</div>
                      </div>
                      <span className="text-xs text-slate-400 flex-shrink-0">{Math.round((a.size || 0) / 1024)} KB</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-col px-5 pb-5 pt-3 border-t border-slate-100 bg-slate-50/30">
            <div className="space-y-2 w-full">
              <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-700 bg-rose-50/60 border border-rose-200 rounded-md p-2.5 w-full" data-testid="pub-privacy-label">
                <input
                  type="checkbox"
                  data-testid="pub-privacy-checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="h-4 w-4 accent-rose-600 mt-0.5 flex-shrink-0"
                />
                <span>
                  <strong className="text-rose-700">*</strong> Ho letto e accetto la <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700">Privacy Policy</a> e acconsento al trattamento dei miei dati personali per la sola finalità di ricevere il preventivo richiesto (GDPR 2016/679). <span className="text-rose-600 font-semibold">Obbligatorio.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-600 bg-emerald-50/40 border border-emerald-200 rounded-md p-2.5 w-full" data-testid="pub-marketing-label">
                <input
                  type="checkbox"
                  data-testid="pub-marketing-checkbox"
                  checked={marketingAccepted}
                  onChange={(e) => setMarketingAccepted(e.target.checked)}
                  className="h-4 w-4 accent-emerald-600 mt-0.5 flex-shrink-0"
                />
                <span>
                  Acconsento a ricevere comunicazioni commerciali, promozioni e newsletter via email/WhatsApp/SMS dall&apos;agenzia. <span className="text-emerald-700 font-medium">(Facoltativo — potrai disiscriverti in qualsiasi momento)</span>
                </span>
              </label>
            </div>
            <div className="flex gap-2 w-full justify-end">
              <Button
                data-testid="pub-review-edit"
                variant="outline"
                onClick={() => setReviewOpen(false)}
                disabled={submitting}
                className="border-slate-300 flex-1 sm:flex-none"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Modifica
              </Button>
              <Button
                data-testid="pub-review-confirm"
                onClick={submit}
                disabled={submitting || !privacyAccepted}
                title={!privacyAccepted ? "Devi accettare la Privacy Policy per procedere" : ""}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white flex-1 sm:flex-none"
              >
                <Sparkles className="w-4 h-4 mr-1.5" /> {submitting ? "Invio in corso…" : "Confermo, invia"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Step({ n, active, done, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
        done ? "bg-emerald-500 text-white"
        : active ? "bg-blue-600 text-white"
        : "bg-slate-200 text-slate-500"
      }`}>{done ? <CheckCircle2 className="w-4 h-4" /> : n}</div>
      <span className={`text-sm ${active ? "font-semibold text-slate-900" : "text-slate-500"}`}>{label}</span>
    </div>
  );
}
